import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, Phone } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const LINKS = [
  { label: "Flyttbidrag", href: "#flyttbidrag", testid: "nav-link-grant" },
  { label: "Tjänster", href: "#tjanster", testid: "nav-link-services" },
  { label: "Vanliga frågor", href: "#faq", testid: "nav-link-faq" },
  { label: "Kontakt", href: "#kontakt", testid: "nav-link-contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : (window.location.href = "/"))}
          className={`font-display font-extrabold text-lg tracking-tight flex items-center gap-2 transition-colors duration-500 ${scrolled ? "text-[#0b0f19]" : "text-white"}`}
        >
          <span className="w-7 h-7 rounded-full bg-[#16a34a] flex items-center justify-center">
            <ArrowUpRight className="w-4 h-4 text-white" strokeWidth={2.5} />
          </span>
          Studentflyttarna
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className={`font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-500 hover:text-[#16a34a] ${scrolled ? "text-slate-600" : "text-slate-300"}`}
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-phone-link"
            href="tel:+46707272941"
            className={`hidden lg:flex items-center gap-2 font-mono2 text-[11px] tracking-wider transition-colors duration-500 hover:text-[#16a34a] ${scrolled ? "text-slate-600" : "text-slate-300"}`}
          >
            <Phone className="w-3.5 h-3.5" /> 070-727 29 41
          </a>
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#offert")}
            className="bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            Få offert
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className={`lg:hidden transition-colors duration-500 ${scrolled ? "text-[#0b0f19]" : "text-white"}`}
          aria-label="Meny"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl text-[#0b0f19] hover:text-[#16a34a] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => go("#offert")}
                className="bg-[#16a34a] text-white font-semibold px-5 py-3 rounded-full w-fit"
              >
                Få offert
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
